import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import { pathOr, map, merge } from 'ramda';
import { Container, Grid, Button, Input, Label } from 'semantic-ui-react';
import classnames from 'classnames';
import DayPicker from 'react-day-picker';
import withStyles from 'react-jss';
import InputMask from 'react-input-mask';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';

import { ClassProp, WithRouterProps } from 'helpers/types';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';

import {
  DateRange,
  PresetButtons,
  presetDateRanges,
  PRESET_BUTTONS,
  isSelectingFirstDay,
  getInitialMonth,
  getInputValue,
  today,
  checkDateRange,
  PresetDateRanges,
} from './DatePicker.helpers';
import styles from './DatePicker.styles';
import { useDatePicker, DatePickerState } from './DatePicker.context';

import {
  useThisYearFilter,
  UseThisYearFilterPayload,
} from './DatePicker.hooks';

type Props = WithRouterProps & {
  classes: ClassProp<typeof styles>;
};

type Params = { merchantId: string; merchantName: string };

export type DateRangeState = DateRange & { enteredTo?: Date };

const defaultState = merge(presetDateRanges.pastNineDays, {
  enteredTo: presetDateRanges.pastNineDays.to,
});

const getInitialState = ({ from, to }: DatePickerState) => ({
  from,
  to,
  enteredTo: to,
});

const renderButton = (
  classes: ClassProp<typeof styles>,
  handleClick: (
    filter: string,
    dateRanges: PresetDateRanges | null,
  ) => () => void,
  activeButton: string,
  { dateRanges, disabled }: UseThisYearFilterPayload,
) => ({ key, label }: PresetButtons) => {
  const isThisYearFilter = key === 'thisYear';
  const btnClass = classnames(classes.button, {
    active: activeButton === key,
  });

  return (
    <Button
      disabled={isThisYearFilter && disabled}
      key={key}
      className={btnClass}
      onClick={handleClick(
        key,
        isThisYearFilter
          ? {
              key: {
                from: dateRanges.from,
                to: dateRanges.to,
              },
            }
          : null,
      )}
    >
      {label}
    </Button>
  );
};

const renderQuickSetButtons = (
  classes: ClassProp<typeof styles>,
  handleClick: (
    filter: string,
    dateRanges: PresetDateRanges | null,
  ) => () => void,
  activeButton: string,
  thisYearFilterPayload: UseThisYearFilterPayload,
) => (
  <Grid.Row className={classes.fixMargins}>
    {map(
      renderButton(classes, handleClick, activeButton, thisYearFilterPayload),
      PRESET_BUTTONS,
    )}
  </Grid.Row>
);

const DatePicker: FunctionComponent<Props> = ({ classes, match }) => {
  const { merchantId } = match.params as Params;
  const DateContext = useDatePicker();
  const [activeButton, setActiveButton] = useState<string>('');
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const thisYearFilterPayload = useThisYearFilter(merchantId);
  const [dateRange, setDateRange] = useState<DateRangeState>(
    getInitialState(DateContext),
  );

  const [inputValue, setInputValue] = useState<string>(
    getInputValue(DateContext),
  );

  const componentRef = useRef<HTMLDivElement>(null);

  const clickOutsideListener = (event: any) => {
    if (componentRef.current && componentRef.current.contains(event.target)) {
      return;
    }

    if (checkDateRange(inputValue).isValid) {
      doneClickHandler();
    }
  };

  useEffect(() => {
    setInputValue(getInputValue(dateRange));
  }, [dateRange]);

  useEffect(() => {
    document.removeEventListener('mousedown', clickOutsideListener);

    if (openCalendar) {
      document.addEventListener('mousedown', clickOutsideListener);
    }

    return () => {
      document.removeEventListener('mousedown', clickOutsideListener);
    };
  }, [openCalendar, inputValue]);

  const showCalendar = () => {
    setOpenCalendar(true);
  };

  const hideCalendar = () => {
    setOpenCalendar(false);
  };

  const handleClick = (
    filter: string,
    dateRanges: PresetDateRanges | null,
  ) => () => {
    const from = pathOr(today, [filter, 'from'], presetDateRanges);
    const to = pathOr(today, [filter, 'to'], presetDateRanges);
    let dateRange = {
      from,
      to,
      enteredTo: to,
    };

    if (dateRanges) {
      dateRange = { ...dateRange, ...dateRanges };
    }

    setActiveButton(filter);
    setDateRange(dateRange);
  };

  const resetClickhandler = () => {
    setDateRange(defaultState);
  };

  const dayClickHandler = (day: Date) => {
    if (isSelectingFirstDay(dateRange, day)) {
      setActiveButton('');
      setDateRange({
        from: day,
        to: null,
      });
    } else {
      setDateRange({
        from,
        to: day,
        enteredTo: day,
      });
    }
  };

  const dayMouseEnterHandler = (day: Date) => {
    if (!isSelectingFirstDay(dateRange, day)) {
      setDateRange({
        from,
        to,
        enteredTo: day,
      });
    }
  };

  const doneClickHandler = () => {
    hideCalendar();

    if (getInputValue(DateContext) !== inputValue) {
      const checkedDateRange = checkDateRange(inputValue);
      if (checkedDateRange.isValid && checkedDateRange.dateRange) {
        const dateRange = checkedDateRange.dateRange;
        DateContext.setDateRange({
          from: dateRange.from,
          to: dateRange.to,
        });
      } else {
        toastMapping(
          {},
          'error',
        )([
          {
            message: 'Invalid Date Range',
            displayMessage: 'Invalid Date Range',
          },
        ]);
      }
    }
  };

  const onChange = (event: any) => {
    const value = event.target.value;
    const getPeriod = checkDateRange(value);

    if (getPeriod.isValid) {
      setActiveButton('');
      setDateRange(getPeriod.dateRange as DateRangeState);
    }

    setInputValue(value);
  };

  const { from, to, enteredTo } = dateRange;
  const modifiers = { start: from, end: enteredTo } as any;
  const disabledDays = { before: from, after: today.toDate() };
  const selectedDays = { from, to: enteredTo };

  return (
    <Container className={classes.calendarWidget}>
      <div ref={componentRef}>
        <div>
          <Label basic horizontal className={classes.label}>
            Date Range
          </Label>

          <Input>
            <InputMask
              onFocus={showCalendar}
              onChange={onChange}
              value={inputValue}
              mask="99/99/99 - 99/99/99"
            />
          </Input>
        </div>

        {openCalendar && (
          <div className={classes.calendarContainer}>
            <Grid className={classes.fixMargins}>
              {renderQuickSetButtons(
                classes,
                handleClick,
                activeButton,
                thisYearFilterPayload,
              )}

              <Grid.Row className={classes.fixMargins}>
                <DayPicker
                  className="selectable"
                  numberOfMonths={2}
                  initialMonth={getInitialMonth()}
                  selectedDays={selectedDays}
                  disabledDays={disabledDays}
                  modifiers={modifiers}
                  onDayClick={dayClickHandler}
                  onDayMouseEnter={dayMouseEnterHandler}
                />
              </Grid.Row>

              <Grid.Row className={classes.buttonBar}>
                {!checkDateRange(inputValue).isValid && (
                  <span className={classes.invalidMessage}>
                    Invalid Date Range
                  </span>
                )}

                <Button
                  onClick={resetClickhandler}
                  className={classes.buttonTertiary}
                >
                  Reset to Default
                </Button>

                <Button
                  onClick={doneClickHandler}
                  disabled={!checkDateRange(inputValue).isValid}
                  className={classnames(
                    classes.button,
                    classes.buttonPrimary,
                    classes.smallBtn,
                  )}
                >
                  Done
                </Button>
              </Grid.Row>
            </Grid>
          </div>
        )}
      </div>
    </Container>
  );
};

export default compose<Props, any>(withRouter, withStyles(styles))(DatePicker);
